import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { fmtKm, fmtUnixDate } from "@/lib/wholesale-format";
import { vatBreakdown } from "@/lib/wholesale-pricing";
import { WHOLESALE_STATUS_LABELS, type WholesaleStatus } from "@/lib/wholesale";

type PublicOrder = {
  number: string;
  status: WholesaleStatus;
  createdAt: number;
  confirmedAt: number | null;
  dueAt: number | null;
  sender: {
    companyName: string; address: string; city: string; postalCode: string;
    country: string; phone: string; email: string; idNumber: string; vatNumber: string;
  } | null;
  senderLogoUrl: string;
  senderBanks: { bankName: string; accountNumber: string }[];
  partner: { name: string; companyIdNumber: string; vatNumber: string; address: string; city: string; postalCode: string };
  items: { name: string; sku: string; quantity: number; unitPrice: number; lineTotal: number }[];
  subtotal: number;
  discountTotal: number;
  vatRate: number;
  total: number;
  note: string;
};

export function WholesaleSharePage() {
  const { token } = useParams({ from: "/vp/$token" });

  const { data, isLoading, isError } = useQuery<PublicOrder>({
    queryKey: ["public-wholesale", token],
    queryFn: async () => {
      const res = await fetch(`/api/public/wholesale/${token}`);
      if (!res.ok) throw new Error("invalid");
      return res.json();
    },
    retry: false,
  });

  const vat = data ? vatBreakdown(data.total, data.vatRate) : null;

  return (
    <div className="doc-page">
      <style>{styles}</style>

      <div className="doc-shell">
        {isLoading && <div className="doc-state">Učitavam predračun…</div>}

        {(isError || (!isLoading && !data)) && (
          <div className="doc-state">
            <h1>Link je istekao</h1>
            <p>Ili nije validan. Zatražite novi link od dobavljača.</p>
          </div>
        )}

        {data && vat && (
          <article className="doc">
            <header className="doc-head">
              <div className="sender">
                {data.senderLogoUrl && <img className="sender-logo" src={data.senderLogoUrl} alt="" />}
                {data.sender && (
                  <>
                    <div className="sender-name">{data.sender.companyName}</div>
                    <div className="sender-lines">
                      <div>{data.sender.address}</div>
                      <div>{data.sender.postalCode} {data.sender.city}</div>
                      {data.sender.idNumber && <div>ID: {data.sender.idNumber}</div>}
                      {data.sender.vatNumber && <div>PDV: {data.sender.vatNumber}</div>}
                      {data.sender.phone && <div>{data.sender.phone}</div>}
                      {data.sender.email && <div>{data.sender.email}</div>}
                    </div>
                  </>
                )}
              </div>

              <div className="doc-id">
                <div className="doc-kind">Predračun</div>
                <div className="doc-number">{data.number}</div>
                <div className="doc-status">{WHOLESALE_STATUS_LABELS[data.status]}</div>
              </div>
            </header>

            <dl className="doc-meta">
              <div>
                <dt>Datum</dt>
                <dd>{fmtUnixDate(data.confirmedAt ?? data.createdAt)}</dd>
              </div>
              {data.dueAt && (
                <div>
                  <dt>Rok plaćanja</dt>
                  <dd>{fmtUnixDate(data.dueAt)}</dd>
                </div>
              )}
              <div className="doc-meta-buyer">
                <dt>Kupac</dt>
                <dd>
                  <div className="buyer-name">{data.partner.name}</div>
                  {data.partner.address && <div>{data.partner.address}</div>}
                  {(data.partner.postalCode || data.partner.city) && (
                    <div>{data.partner.postalCode} {data.partner.city}</div>
                  )}
                  {data.partner.companyIdNumber && <div>ID broj: {data.partner.companyIdNumber}</div>}
                  {data.partner.vatNumber && <div>PDV broj: {data.partner.vatNumber}</div>}
                </dd>
              </div>
            </dl>

            <table className="items">
              <thead>
                <tr>
                  <th scope="col">Artikal</th>
                  <th scope="col" className="num">Kol.</th>
                  <th scope="col" className="num">Cijena</th>
                  <th scope="col" className="num">Iznos</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={i}>
                    <td>
                      <div className="item-name">{it.name}</div>
                      {it.sku && <div className="item-sku">{it.sku}</div>}
                    </td>
                    <td className="num">{it.quantity}</td>
                    <td className="num">{fmtKm(it.unitPrice)}</td>
                    <td className="num strong">{fmtKm(it.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="sums">
              <div className="sum-row">
                <span>Osnovica</span>
                <span className="num">{fmtKm(vat.net)}</span>
              </div>
              {data.discountTotal > 0 && (
                <div className="sum-row">
                  <span>Rabat</span>
                  <span className="num">− {fmtKm(data.discountTotal)}</span>
                </div>
              )}
              <div className="sum-row">
                <span>PDV {data.vatRate}%</span>
                <span className="num">{fmtKm(vat.vat)}</span>
              </div>
            </div>

            {/* Iznos i rok u jednoj crnoj traci — isti par koji stoji i na preview kartici u chatu. */}
            <div className="total-slab">
              <div className="total-label">Za uplatu</div>
              <div className="total-amount">{fmtKm(data.total)}</div>
              {data.dueAt && <div className="total-due">do {fmtUnixDate(data.dueAt)}</div>}
            </div>

            {data.senderBanks.length > 0 && (
              <section className="note">
                <div className="note-label">Uplata na račun</div>
                {data.senderBanks.map((b, i) => (
                  <p key={i}>{b.bankName} — <strong>{b.accountNumber}</strong></p>
                ))}
                <p>Poziv na broj: {data.number}</p>
              </section>
            )}

            {data.note && (
              <section className="note">
                <div className="note-label">Napomena</div>
                <p>{data.note}</p>
              </section>
            )}

            <a className="download" href={`/api/public/wholesale/${token}/predracun.pdf`} target="_blank" rel="noreferrer">
              <Download className="download-icon" strokeWidth={1.75} />
              Preuzmi predračun (PDF)
            </a>

            {data.sender && <footer className="doc-foot">{data.sender.companyName}</footer>}
          </article>
        )}
      </div>
    </div>
  );
}

const styles = `
  .doc-page {
    --paper: #ffffff;
    --ground: #eceae5;
    --ink: #111111;
    --ink-soft: #5c5a54;
    --ink-faint: #918e86;
    --rule: #d8d5cd;
    min-height: 100vh;
    background: var(--ground);
    color: var(--ink);
    font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    padding: 16px 12px 48px;
    overflow-x: hidden;
  }
  @media (min-width: 640px) { .doc-page { padding: 48px 16px 72px; } }

  .doc-shell { max-width: 640px; margin: 0 auto; }

  .doc-state {
    background: var(--paper);
    border: 1px solid var(--rule);
    padding: 48px 24px;
    text-align: center;
    color: var(--ink-soft);
    font-size: 13px;
  }
  .doc-state h1 { font-size: 18px; margin: 0 0 8px; color: var(--ink); }
  .doc-state p { margin: 0; }

  .doc {
    background: var(--paper);
    border: 1px solid var(--rule);
    padding: 24px 18px 18px;
    animation: rise 420ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }
  @media (min-width: 640px) { .doc { padding: 40px 40px 28px; } }
  @keyframes rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) { .doc { animation: none; } }

  .doc-head {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 20px;
    border-bottom: 2px solid var(--ink);
  }
  @media (min-width: 640px) { .doc-head { flex-direction: row; justify-content: space-between; gap: 24px; } }

  .sender-logo { max-height: 44px; max-width: 180px; display: block; margin-bottom: 10px; }
  .sender-name { font-size: 14px; font-weight: 700; letter-spacing: 0.04em; }
  .sender-lines { margin-top: 4px; font-size: 11px; line-height: 1.7; color: var(--ink-soft); }

  .doc-id { flex-shrink: 0; }
  @media (min-width: 640px) { .doc-id { text-align: right; } }
  .doc-kind { font-size: 11px; letter-spacing: 0.34em; text-transform: uppercase; color: var(--ink-faint); }
  .doc-number { font-size: 26px; font-weight: 700; margin-top: 2px; font-variant-numeric: tabular-nums; }
  .doc-status {
    display: inline-block;
    margin-top: 6px;
    padding: 2px 8px;
    border: 1px solid var(--ink);
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .doc-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;
    margin: 20px 0 0;
    font-size: 12px;
  }
  .doc-meta dt { font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-faint); }
  .doc-meta dd { margin: 4px 0 0; line-height: 1.7; color: var(--ink-soft); font-variant-numeric: tabular-nums; }
  .doc-meta-buyer { grid-column: 1 / -1; }
  .buyer-name { color: var(--ink); font-weight: 700; }

  .items { width: 100%; border-collapse: collapse; margin-top: 24px; font-size: 12px; }
  .items th {
    text-align: left;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink-faint);
    font-weight: 400;
    padding: 0 0 8px;
    border-bottom: 1px solid var(--rule);
  }
  .items td { padding: 12px 0; border-bottom: 1px solid var(--rule); vertical-align: top; }
  .items .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; padding-left: 12px; }
  .items .strong { font-weight: 700; }
  .item-name { line-height: 1.5; }
  .item-sku { margin-top: 3px; font-size: 10px; color: var(--ink-faint); }

  .sums { margin-top: 16px; display: flex; flex-direction: column; gap: 6px; font-size: 12px; color: var(--ink-soft); }
  .sum-row { display: flex; justify-content: space-between; gap: 16px; }
  .sum-row .num { font-variant-numeric: tabular-nums; color: var(--ink); }

  .total-slab {
    margin-top: 18px;
    background: var(--ink);
    color: var(--paper);
    padding: 18px 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 16px;
  }
  .total-label { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; opacity: 0.65; width: 100%; }
  .total-amount { font-size: 30px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .total-due { margin-left: auto; font-size: 12px; opacity: 0.75; font-variant-numeric: tabular-nums; }

  .note { margin-top: 20px; border-left: 2px solid var(--rule); padding-left: 14px; }
  .note-label { font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-faint); }
  .note p { margin: 5px 0 0; font-size: 12px; line-height: 1.7; color: var(--ink-soft); }

  .download {
    margin-top: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 13px 16px;
    border: 1px solid var(--ink);
    color: var(--ink);
    text-decoration: none;
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    transition: background 140ms ease, color 140ms ease;
  }
  .download:hover, .download:focus-visible { background: var(--ink); color: var(--paper); }
  .download:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
  .download-icon { width: 15px; height: 15px; }

  .doc-foot {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid var(--rule);
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink-faint);
    text-align: center;
  }
`;
